import { FC } from 'react';

import { isAccountStatusReadOnly } from 'utils/clickme';
import { BannerModerationStatus } from 'utils/constants/banner';
import { CampaignType } from 'utils/constants/campaign';
import { CampaignStatus } from 'utils/constants/dashboard';
import { VIEW_DATE_WITH_MONTH_FORMAT } from 'utils/constants/date';
import { formatDate } from 'utils/date';

import dashboardBannerButtonClick from '@hh.ru/analytics-js-events/build/clickme/employer/common/dashboard_banner_button_click';
import { VSpacing, VSpacingContainer } from '@hh.ru/magritte-ui';
import { Link } from '@hh.ru/redux-spa-middleware';

import Button, { ButtonAppearance, ButtonIconPosition } from 'bloko/blocks/button';
import Icon, { IconScale, IconView } from 'bloko/blocks/icon';

import useTrls from 'hooks/translation/useTrls';

import BanerAdminActions from 'components/BanerAdminActions';
import StatisticLink from 'components/StatisticLink';

import Actions from 'components/Banner/Actions';
import Preview from 'components/Banner/Preview';
import DashboardAveragePricePerClick from 'components/Dashboard/DashboardAveragePricePerClick/DashboardAveragePricePerClick';

import { FrontendStatusType, State, Statistics } from 'store/models/dashboard';
import { useSelector } from 'store/utils';

import styles from 'components/Banner/Banner.module.less';

const TrlKeys = {
    edit: 'dashboard.CampaignList.Banner.edit',
};

interface BannerProps {
    campaignStatus: FrontendStatusType;
    campaignId: number;
    id: number;
    title: string;
    imageUrl: string;
    text: string;
    statistics: Statistics;
    createdAt: string;
    states: State[];
    vacancyId: number | null;
    enabled: boolean;
    moderationStatus: BannerModerationStatus;
    bannersCount: number;
    isBannerLastEnabled: boolean;
    hasVideo: boolean;
    url: string;
    bannedAsAdvertisement: boolean;
    bannedForAllSources: boolean;
    advertisingServiceType: CampaignType;
}

const Banner: FC<BannerProps> = ({
    campaignId,
    id: bannerId,
    title,
    imageUrl,
    text,
    statistics,
    createdAt,
    campaignStatus,
    states,
    vacancyId,
    enabled,
    moderationStatus,
    bannersCount,
    isBannerLastEnabled,
    hasVideo,
    url,
    bannedAsAdvertisement,
    bannedForAllSources,
    advertisingServiceType,
}) => {
    const { averagePricePerClickInCents } = statistics;
    const urlToStatistics = `/new/banner/${bannerId}/statistics/`;
    const urlToBannerEdit = `/new/campaign/${campaignId}/banner/${bannerId}/`;

    const currentAccountStatus = useSelector(({ user }) => user.user?.currentAccount.status);
    const isArchivedCampaign = campaignStatus === CampaignStatus.Archived;
    const isInformationCampaign = advertisingServiceType === CampaignType.Information;
    const isReadOnlyAccount = isAccountStatusReadOnly(currentAccountStatus);
    const showEditButton = isInformationCampaign && !isArchivedCampaign && !isReadOnlyAccount;

    const trls = useTrls();

    const sendAnalytics = () => dashboardBannerButtonClick({ buttonName: 'banner_edit_button', campaignId, bannerId });

    return (
        <div className={styles.banner} data-qa="ad-block">
            <div className={styles.bannerContent}>
                <div className={styles.bannerImage}>
                    <Preview
                        campaignId={campaignId}
                        bannerId={bannerId}
                        title={title}
                        imageUrl={imageUrl}
                        frontendStatus={campaignStatus}
                        text={text}
                        vacancyId={vacancyId}
                        hasVideo={hasVideo}
                        url={url}
                        isCampaignArchived={isArchivedCampaign}
                        isReadOnlyAccount={isReadOnlyAccount}
                        isInformationCampaign={isInformationCampaign}
                    />
                </div>
                <div className={styles.bannerCreated}>{formatDate(createdAt, VIEW_DATE_WITH_MONTH_FORMAT)}</div>
                <div className={styles.bannerStatistics}>
                    <VSpacingContainer default={16}>
                        <StatisticLink
                            {...statistics}
                            action={urlToStatistics}
                            dataQa={`banner_statistics_${bannerId}`}
                            campaignId={campaignId}
                            bannerId={bannerId}
                        />
                        {!!averagePricePerClickInCents && (
                            <DashboardAveragePricePerClick
                                averagePricePerClickInCents={averagePricePerClickInCents}
                                urlToStatistics={urlToStatistics}
                                dataQa={`banner_average_price_per_click_${bannerId}`}
                            />
                        )}
                    </VSpacingContainer>
                </div>
                <div className={styles.bannerActions}>
                    <Actions
                        bannerId={bannerId}
                        campaignStatus={campaignStatus}
                        campaignId={campaignId}
                        states={states}
                        enabled={enabled}
                        moderationStatus={moderationStatus}
                        bannersCount={bannersCount}
                        isBannerLastEnabled={isBannerLastEnabled}
                        bannedAsAdvertisement={bannedAsAdvertisement}
                        bannedForAllSources={bannedForAllSources}
                    />
                    <BanerAdminActions campaignId={campaignId} bannerId={bannerId} />
                </div>
            </div>

            {showEditButton && (
                <>
                    <VSpacing default={12} />
                    <div className={styles.bannerButtons}>
                        <Button
                            onClick={sendAnalytics}
                            appearance={ButtonAppearance.Filled}
                            icon={<Icon view={IconView.Gear} scale={IconScale.Small} />}
                            iconPosition={ButtonIconPosition.Left}
                            Element={Link}
                            to={urlToBannerEdit}
                            data-qa="banner-edit-button"
                        >
                            {trls(TrlKeys.edit)}
                        </Button>
                    </div>
                </>
            )}
        </div>
    );
};

export default Banner;
